import type { ErrorPageProps } from "../ui/blocks/error-page/error-page";

/**
 * Enhanced Error interface that can carry ErrorPage props
 */
export interface CustomError extends Error {
  digest?: string;
  errorCode?: "404" | "500";
  title?: string;
  description?: string[];
  buttonText?: string;
}

/**
 * Extracts ErrorPage props from an error object
 */
export function extractErrorPageProps(
  error?: CustomError
): Partial<ErrorPageProps> {
  if (!error) {
    return {};
  }

  // Extract from encoded message
  if (error.message?.startsWith("CUSTOM_ERROR:")) {
    try {
      const jsonString = error.message.replace("CUSTOM_ERROR:", "");
      const errorData = JSON.parse(jsonString);
      if (errorData.customProps) {
        return errorData.customProps;
      }
    } catch (_parseError) {
      // Parse error ignored - return undefined for invalid JSON
    }
  }

  return {};
}

/**
 * Creates a custom error with ErrorPage props
 * Usage: throw createCustomError('Something went wrong', { errorCode: '500', title: 'Custom Title' })
 */
export function createCustomError(
  message: string,
  errorPageProps: Partial<
    Pick<ErrorPageProps, "errorCode" | "title" | "description" | "buttonText">
  >
): CustomError {
  const errorData = {
    originalMessage: message,
    customProps: errorPageProps,
  };

  const error = new Error(
    `CUSTOM_ERROR:${JSON.stringify(errorData)}`
  ) as CustomError;

  return error;
}
